import { Box, Skeleton, Stack, Typography } from '@mui/material'
import { Divider, SkeletonText } from 'utoniq-ui'

import {
  Maybe,
  PaymentDetailFragment,
  PaymentMethod,
  PreSale,
  PreSaleLotteryConfirmationFragment,
} from '@/clients/utoniq-core/schema'
import { BaseLayout } from '@/components/layouts'
import { PaymentProgressModal } from '@/components/organisms/functionals'
import {
  ExpiredCardModal,
  PaymentErrorModal,
  Require3DSecureModal,
} from '@/components/organisms/functionals/PaymentErrorModal'
import { ApplyPreSaleLotteryValue } from '@/components/pages/LotteryDetail/template/LotteryDetailForm'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'

import {
  ButtonCell,
  FirstComeFirstServedPlansCell,
  FirstComeFirstServedSubTitleCell,
  PaymentCell,
  PaymentInfoCell,
} from './components'

export * from './fragments'

export type State = {
  loading: boolean
  submitting: boolean
  preSale:
    | (PreSaleLotteryConfirmationFragment &
        Pick<PreSale, 'firstComeFirstServed'>)
    | undefined
  paymentSource: Maybe<PaymentDetailFragment> | undefined
  choice: ApplyPreSaleLotteryValue | undefined
  billingStatements:
    | {
        statements: {
          label: Maybe<string> | undefined
          value: string
        }[]
        totalPrice: number
      }[]
    | undefined
  paymentMethod: PaymentMethod
  openExpiredCardModal: boolean
  openPaymentErrorModal: boolean
  openRequire3DSecureModal: boolean
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onSubmit: () => Promise<void>
  onCloseRequire3DSecureModal: () => void
  onCloseExpiredCardModal: () => void
  onClosePaymentErrorModal: () => void
}

export const FirstComeFirstServedConfirmationTemplate: Template<State, Action> =
  ({
    state,
    onSubmit,
    onCloseRequire3DSecureModal,
    onCloseExpiredCardModal,
    onClosePaymentErrorModal,
  }) => {
    const { t } = useTranslation('firstComeFirstServedConfirmation')
    const { routerPages, generatePath, router } =
      useRouter<{ preSaleId: string }>()
    return (
      <BaseLayout
        header={{
          back: true,
          defaultBackPage: generatePath(
            routerPages.FirstComeFirstServedDetail,
            {
              preSaleId: router.query.preSaleId ?? '#',
            }
          ),
          title: t('title'),
        }}
      >
        {state.loading && !state.preSale ? (
          <Box sx={{ px: 5 }}>
            <TitleSkeletonCell />
            <SubTitleSkeletonCell />
            <PlansSkeletonCell />
            <PaymentInfoSkeletonCell />
            <PaymentSkeletonCell />
            <ButtonSkeletonCell />
          </Box>
        ) : (
          <>
            <Box sx={{ px: 5 }}>
              <FirstComeFirstServedTitleCell />
              <FirstComeFirstServedSubTitleCell preSale={state?.preSale} />
              <FirstComeFirstServedPlansCell
                choice={state.choice}
                items={state?.preSale?.items}
              />
              <PaymentInfoCell
                paymentSource={state.paymentSource}
                paymentMethod={state.paymentMethod}
              />
              <PaymentCell
                billingStatements={state.billingStatements}
                choiceLength={1}
              />
              <ButtonCell onSubmit={onSubmit} isSubmitting={state.submitting} />
            </Box>
          </>
        )}
        <Require3DSecureModal
          open={state.openRequire3DSecureModal}
          onClose={onCloseRequire3DSecureModal}
        />

        <ExpiredCardModal
          open={state.openExpiredCardModal}
          onClose={onCloseExpiredCardModal}
        />

        <PaymentErrorModal
          open={state.openPaymentErrorModal}
          onClose={onClosePaymentErrorModal}
        />
        <PaymentProgressModal open={state.submitting} />
      </BaseLayout>
    )
  }

const FirstComeFirstServedTitleCell: Cell = () => {
  const { t } = useTranslation('firstComeFirstServedConfirmation')
  return (
    <CellFrame sx={{ py: 5 }}>
      <Typography variant="h4">
        {t('confirmPurchaseDetail', '購入内容の確認')}
      </Typography>
    </CellFrame>
  )
}

const TitleSkeletonCell: Cell = () => {
  return (
    <CellFrame sx={{ py: 5 }}>
      <SkeletonText variant="h4" width="50%" />
    </CellFrame>
  )
}

const SubTitleSkeletonCell: Cell = () => {
  return (
    <CellFrame sx={{ pb: 5 }}>
      <Stack sx={{ py: 1, bgcolor: 'grey.50' }}>
        <SkeletonText variant="h4" />
      </Stack>
      <Stack sx={{ pt: 3 }} spacing={2}>
        {Array.from(Array(2).keys()).map(index => (
          <Stack key={index} spacing={2}>
            <Stack>
              <SkeletonText variant="body2" numberOfLine={2} />
            </Stack>
            <Divider />
          </Stack>
        ))}
      </Stack>
    </CellFrame>
  )
}

const PlansSkeletonCell: Cell = () => {
  return (
    <CellFrame sx={{ pb: 5 }}>
      <Stack sx={{ py: 1, bgcolor: 'grey.50' }}>
        <SkeletonText variant="h4" />
      </Stack>
      <SkeletonText variant="body2" width="10%" />
      <Stack sx={{ pt: 3 }} spacing={2}>
        <Stack spacing={2}>
          <Stack>
            <SkeletonText variant="body2" numberOfLine={2} />
          </Stack>
          <Divider />
        </Stack>
      </Stack>
    </CellFrame>
  )
}

const PaymentInfoSkeletonCell: Cell = () => {
  return (
    <CellFrame sx={{ pb: 5 }}>
      <Stack sx={{ py: 1, bgcolor: 'grey.50' }}>
        <SkeletonText variant="h4" />
      </Stack>
      <Stack sx={{ pt: 3 }} spacing={2}>
        <Stack direction="row" spacing={3} alignItems="center" sx={{ py: 5 }}>
          <Stack flex={1}>
            <Skeleton variant="rectangular" width="100%" height={40} />
          </Stack>
          <Stack flex={1}>
            <SkeletonText variant="caption" width="50%" />
          </Stack>
        </Stack>
        <Divider />
      </Stack>
    </CellFrame>
  )
}

const PaymentSkeletonCell: Cell = () => {
  return (
    <CellFrame sx={{ pb: 5 }}>
      <Stack sx={{ py: 1, bgcolor: 'grey.50' }}>
        <SkeletonText variant="h4" />
      </Stack>
      <Stack>
        <Stack direction="row" justifyContent="space-between" sx={{ py: 2 }}>
          <SkeletonText variant="subtitle1" width="30%" />
          <SkeletonText variant="subtitle1" width="30%" />
        </Stack>
        <Divider />
        <Stack direction="row" justifyContent="space-between" sx={{ py: 2 }}>
          <SkeletonText variant="subtitle1" width="30%" />
          <SkeletonText variant="subtitle1" width="30%" />
        </Stack>
        <Divider />
        <Stack direction="row" justifyContent="space-between" sx={{ py: 2 }}>
          <SkeletonText variant="subtitle1" width="30%" />
          <SkeletonText variant="subtitle1" width="30%" />
        </Stack>
      </Stack>
    </CellFrame>
  )
}

const ButtonSkeletonCell: Cell = () => {
  return (
    <CellFrame sx={{ py: 4 }}>
      <Stack flex={1} alignItems="center">
        <Skeleton
          variant="rectangular"
          width={244}
          height={44}
          sx={{ borderRadius: '34px' }}
        />
      </Stack>
    </CellFrame>
  )
}
